import { isDailyRent } from './category/isDailyRent';
import { getNewbuildingFeaturePromoLabels } from './labels/getNewbuildingFeaturePromoLabels';
import { getNewbuildingKpnLabels } from './labels/getNewbuildingKpnLabels';
import { getNewbuildingSnippetLabels } from './labels/getNewbuildingSnippetLabels';
import { getOnlineRentalAgreementLabel } from './labels/getOnlineRentalAgreementLabel';
import { getPledgeRealtyLabel } from './labels/getPledgeRealtyLabel';
import { getRosreestrCheckedLabel } from './labels/getRosreestrCheckedLabel';
import { getTradeRealtyLabel } from './labels/getTradeRealtyLabel';
import { EOfferFeatureLabelsV2Types } from '../../../../../shared/repositories/search-offers.legacy/v2/types';
import { TComputedPropMapper } from '../../types/computed-props-mapper';
import { ELabelColor, ILabel } from '../../types/label';
import { getIsSoldFurnishedLabelText } from '../getIsSoldFurnishedLabelText';
import { prepareGoodPriceLabel } from '../prepareGoodPriceLabel';
import { prepareNotApprovedLayoutLabel } from '../prepareNotApprovedLayoutLabel';
import { getSubsidisedMortgageLabelText, isSubsidisedMortgageAvailable } from '../subsidisedMortgage';
import { EOfferLabels } from 'shared/repositories/search-offers.legacy/v2/types/dailyrentData';

const LABEL_TYPE_TO_COLOR: Record<EOfferFeatureLabelsV2Types, ELabelColor> = {
  [EOfferFeatureLabelsV2Types.DiscountMortgage]: ELabelColor.Purple,
};

export const labels: TComputedPropMapper<ILabel[]> = (
  { api, offer, position, jsonQuery, isRosreestrCheckedLabelAvailable, isNewbuildingBookedFromDeveloper },
  { config },
) => {
  /**
   * @todo Удалить фича-тогл
   * @description Данный функционал появился в задаче CD-187921, будет удалён в задаче CD-189395
   */
  const isElectronicTradingEnabled = config.getStrict<boolean>('Audience.ElectronicTrading.Enabled');

  const isDailyRentOffer = isDailyRent(offer);
  const showInstantBookingLabel = offer.dailyrent?.offerLabels.includes(EOfferLabels.InstantBooking);

  const isSearchFlatShareEnabled = config.getStrict<boolean>('frontend_search_flat_share_enabled.Enabled');
  const offerLabels: string[] = offer.offerFeatureLabels || [];
  const offerLabelsV2 = offer.offerFeatureLabelsV2 || [];

  const subsidisedMortgageAvailable = isSubsidisedMortgageAvailable(offer, config);
  const goodPriceLabel = prepareGoodPriceLabel(offer);
  const layoutLabel = prepareNotApprovedLayoutLabel(offer);

  let labels: ILabel[] = [];

  const labelsV1: ILabel[] = offerLabels.map(text => ({ color: ELabelColor.DarkGray, text }));
  const labelsV2: ILabel[] = offerLabelsV2.map(({ text, type }) => ({ color: LABEL_TYPE_TO_COLOR[type], text }));

  labels.push(...labelsV1);

  labels.unshift(...labelsV2);

  if (offer.isOnlineRentalAgreementEnabled) {
    labels.unshift(getOnlineRentalAgreementLabel());
  }

  if (!isDailyRentOffer && offer.isUnique && !offer.promoInfo?.isExclusive) {
    labels.unshift({ color: ELabelColor.Blue, text: 'Только на Циан' });
  }

  if (showInstantBookingLabel) {
    labels.push({
      color: ELabelColor.BlueInstantBooking,
      text: 'Мгновенная бронь',
      tooltip: {
        description:
          'Это жильё можно забронировать без переговоров с владельцем и долгого ожидания подтверждения брони',
      },
    });
  }

  if (offer.isVerifiedByCian) {
    labels.unshift({
      color: ELabelColor.BlueVerifiedByCian,
      text: 'Проверено Циан',
      tooltip: {
        description:
          'Убедились, что информация в объявлении верна — оценили видео, фото объекта или дополнительно проанализировали объявление',
        link: 'https://support.cian.ru/ru/knowledge_base/article/7516/category/463',
        linkText: 'Подробнее',
      },
    });
  }

  if (isNewbuildingBookedFromDeveloper) {
    labels.unshift({ text: 'Забронирована', color: ELabelColor.DarkGray });
  }

  if (offer.isSoldFurnished) {
    labels.unshift({ color: ELabelColor.Orange, text: getIsSoldFurnishedLabelText(config) });
  }

  if (goodPriceLabel) {
    labels.unshift(goodPriceLabel);
  }

  const extensionLabels = offer.extensionLabels?.map(({ value }) => ({ text: value, color: ELabelColor.Yellow })) || [];
  labels = labels.concat(extensionLabels);

  if (isRosreestrCheckedLabelAvailable) {
    labels.push(getRosreestrCheckedLabel(offer.offerType, config));
  }

  if (isSearchFlatShareEnabled && layoutLabel) {
    labels.unshift(layoutLabel);
  }

  if (isElectronicTradingEnabled) {
    const isPledgeRealty = !!offer.user?.agentLists?.includes('pledgeRealty');
    const isTradeRealty = !!offer.user?.agentLists?.includes('tradeRealty');

    if (isPledgeRealty) {
      labels.push(getPledgeRealtyLabel());
    }

    if (isTradeRealty) {
      labels.push(getTradeRealtyLabel());
    }
  }

  if (subsidisedMortgageAvailable) {
    labels.unshift({ color: ELabelColor.OrangePercent, text: getSubsidisedMortgageLabelText(config) });
  }

  if (offer.isEarlyAccessEnabled) {
    labels.unshift({
      color: ELabelColor.PurpleEarlyAccess,
      text: 'Ранний доступ',
      tooltip: {
        description:
          'Это жилье недавно опубликовали. Вы можете связаться с арендодателем раньше остальных: первые несколько часов просмотр контактов платный',
      },
    });
  }

  // Новостройки КпН
  if (offer.newbuilding?.isFromLeadFactory) {
    return getNewbuildingKpnLabels({
      labelsV1,
      labelsV2,
      extensionLabels,
      api,
      offer,
      position,
      jsonQuery,
    });
  }

  // Новостройки CPL
  if (offer.newbuilding?.snippetType) {
    return getNewbuildingSnippetLabels({
      labelsV1,
      labelsV2,
      promoLabels: getNewbuildingFeaturePromoLabels(offer),
      api,
      offer,
      position,
      jsonQuery,
    });
  }

  return labels;
};
